
import { Union } from "../../fable_modules/fable-library-js.5.3.0/Types.js";
import { union_type } from "../../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { printf, toText } from "../../fable_modules/fable-library-js.5.3.0/String.js";
import { toString } from "../../fable_modules/fable-library-js.5.3.0/Date.js";
import { PledgeCalculation_calculateDonationEstimate } from "../Pledge.js";
import { orElse, map, defaultArg } from "../../fable_modules/fable-library-js.5.3.0/Option.js";
import { fromParts } from "../../fable_modules/fable-library-js.5.3.0/Decimal.js";
import { UnitName_singular } from "../Challenge.js";
import { IconWeight, IconSize, icon, IconName } from "../Icons.js";
import { textColor_textPrimary600, textColor_textSuccess600, textColor_textNeutral500, textColor_textWarning700 } from "../TailwindExtensions.js";
import { HtmlHelper_createElement } from "../../fable_modules/Feliz.3.3.3/Html.fs.js";
import { fontWeight_fontMedium, fontSize_textXs, alignItems_itemsCenter, display_flex, tw } from "../../fable_modules/Feliz.Tailwind.4.0.2/Strings.fs.js";
import { singleton, ofArray } from "../../fable_modules/fable-library-js.5.3.0/List.js";
import { createElement } from "react";
import { defaultOf } from "../../fable_modules/fable-library-js.5.3.0/Util.js";

/**
 * The six pledge indicator states for browse cards
 */
export class IndicatorVariant extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Supporting", "DonationDue", "NoDonationDue", "Donated", "PledgeAbandoned", "ChallengeCancelled"];
    }
}

export function IndicatorVariant_$reflection() {
    return union_type("Shared.PledgeIndicatorBadge.IndicatorVariant", [], IndicatorVariant, () => [[], [], [], [], [], []]);
}

/**
 * Classify a pledge summary into an indicator variant based on pledge status + challenge status
 */
export function classifyIndicator(pledge) {
    const matchValue = pledge.Status;
    switch (matchValue) {
        case "paid":
            return new IndicatorVariant(3, []);
        case "no_donation_due":
            return new IndicatorVariant(2, []);
        case "cancelled":
            return new IndicatorVariant(4, []);
        case "abandoned":
            return new IndicatorVariant(4, []);
        case "active": {
            const matchValue_1 = pledge.Challenge.ChallengeStatus;
            switch (matchValue_1) {
                case "published":
                case "pledged":
                case "active":
                    return new IndicatorVariant(0, []);
                case "finalizing":
                case "finalized":
                    return new IndicatorVariant(1, []);
                case "abandoned":
                    return new IndicatorVariant(5, []);
                default:
                    return undefined;
            }
        }
        default:
            return undefined;
    }
}

function formatCurrency(amount) {
    return toText(printf("$%.2f"))(amount);
}

function formatDate(date) {
    return toString(date, "MMM d, yyyy");
}

/**
 * Get the label text for an indicator variant
 */
export function indicatorLabel(variant, pledge) {
    switch (variant.tag) {
        case 1: {
            const arg_2 = formatCurrency(PledgeCalculation_calculateDonationEstimate(pledge.AmountPerUnit, pledge.GoalUnitsAtPledge, pledge.Challenge.UnitsCompleted, pledge.CapAtGoal));
            return toText(printf("You owe: %s"))(arg_2);
        }
        case 2:
            return "No donation due";
        case 3: {
            const arg_3 = formatCurrency(defaultArg(pledge.PledgedAmount, fromParts(0, 0, 0, false, 0)));
            return toText(printf("You donated: %s"))(arg_3);
        }
        case 4: {
            const date_1 = defaultArg(map(formatDate, orElse(pledge.CancelledAt, pledge.AbandonedAt)), "unknown");
            return toText(printf("Abandoned %s"))(date_1);
        }
        case 5:
            return "Pledge voided";
        default: {
            const unit = UnitName_singular(pledge.Challenge.UnitName);
            const arg = formatCurrency(pledge.AmountPerUnit);
            return toText(printf("Your pledge: %s/%s"))(arg)(unit);
        }
    }
}

/**
 * Get the icon name for an indicator variant
 */
export function indicatorIcon(variant) {
    switch (variant.tag) {
        case 1:
            return new IconName(34, []);
        case 2:
            return new IconName(32, []);
        case 3:
            return new IconName(32, []);
        case 4:
            return new IconName(7, []);
        case 5:
            return new IconName(7, []);
        default:
            return new IconName(3, []);
    }
}

/**
 * Get the text color class for an indicator variant
 */
export function indicatorColorClass(variant) {
    switch (variant.tag) {
        case 1:
            return textColor_textWarning700;
        case 2:
            return textColor_textNeutral500;
        case 3:
            return textColor_textSuccess600;
        case 4:
            return textColor_textNeutral500;
        case 5:
            return textColor_textNeutral500;
        default:
            return textColor_textPrimary600;
    }
}

/**
 * Render the pledge indicator badge. Returns nothing if pledge is None or unclassifiable.
 */
export function pledgeIndicatorBadge(pledge) {
    if (pledge != null) {
        const p = pledge;
        const matchValue = classifyIndicator(p);
        if (matchValue != null) {
            const variant = matchValue;
            const colorClass = indicatorColorClass(variant);
            const label = indicatorLabel(variant, p);
            const iconName = indicatorIcon(variant);
            return HtmlHelper_createElement("div", ofArray([["className", tw(ofArray([display_flex, alignItems_itemsCenter, "gap-1.5", colorClass, fontSize_textXs, fontWeight_fontMedium]))], ["children", [createElement(icon, {
                name: iconName,
                size: new IconSize(0, []),
                weight: new IconWeight(1, []),
            }), HtmlHelper_createElement("span", singleton(["children", singleton(label)]))]]]));
        }
        else {
            return defaultOf();
        }
    }
    else {
        return defaultOf();
    }
}

//# sourceMappingURL=PledgeIndicatorBadge.js.map
